import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Select, Popover, Tooltip, Button } from "antd";
import {
  getBoardPay,
  getBoardPayProfiles,
} from "../redux/actions/CompanyAction";
import {
  boardPayMap,
  formatMeasure,
  emdash,
} from "../utils/Constants/systemSetting";
import BarGraphOverview from "../components/Graphs/BarGraphOverview";
import LineGraph from "../components/Graphs/LineGraph";
import { Link } from "react-router-dom";
import slugifyName from "../utils/slugifyName";
import { exportToCSV } from "../utils/ExecutiveData/csvExport";
import getCIKtickerMap from "../utils/Constants/getCIKtickerMap";

export default function BoardPay({ companyInfo, PDFprocessing }) {
  const version = localStorage.getItem("version") || "1.0.0";
  const dispatch = useDispatch();
  const user = useSelector((state) => state.UserReducer);
  const { subscription } = useSelector((state) => state.CustomerReducer);
  const isTier2 = useMemo(() => {
    if (user?.tier2access || subscription?.plan?.metadata?.isTier2 === "true") {
      return true;
    }
    return false;
  }, [user, subscription]);
  const { boardPay } = useSelector((state) => state.CompanyReducer);
  const { boardPayProfiles } = useSelector((state) => state.CompanyReducer);

  const { companies } = useSelector((state) => state.CompaniesReducer);

  const [yearSelected, setYearSelected] = useState("");

  const [dataPeekLinks, setDataPeekLinks] = useState([]);

  const [CSVExportOptions, setCSVExportOptions] = useState([
    { value: "company", label: "Company" },
  ]);
  const [CSVExportOptionSelected, setCSVExportOptionSelected] =
    useState("company");

  const [customUserPeerGroups, setCustomUserPeerGroups] = useState(
    user?.customPeerGroups || {},
  );

  useEffect(() => {
    const CSVoptions = [];
    CSVoptions.push({
      value: "company",
      label: "Company",
      key: null,
    });
    if (companyInfo?.AnalystCohort) {
      CSVoptions.push({
        value: "analyst",
        label: "Analyst Cohort",
        key: "AnalystCohort",
      });
    }
    if (companyInfo?.ProxyCohort) {
      CSVoptions.push({
        value: "proxy",
        label: "Proxy Cohort",
        key: "ProxyCohort",
      });
    }

    if (companyInfo?.ProxyCohortCompensation) {
      CSVoptions.push({
        value: "proxyCompensation",
        label: "Proxy Cohort (Compensation)",
        key: "ProxyCohortCompensation",
      });
    }
    if (companyInfo?.ProxyCohortPerformance) {
      CSVoptions.push({
        value: "proxyPerformance",
        label: "Proxy Cohort (Performance)",
        key: "ProxyCohortPerformance",
      });
    }

    if (companyInfo?.CompAIProxyCohort) {
      CSVoptions.push({
        value: "compAIproxy",
        label: "Comp AI Proxy Cohort",
        key: "AnalystCohort",
      });
    }

    if (companyInfo?.CompAIProxyCohortCompensation) {
      CSVoptions.push({
        value: "compAIproxyCompensation",
        label: "Comp AI Proxy Cohort (Compensation)",
        key: "CompAIProxyCohortCompensation",
      });
    }
    if (companyInfo?.CompAIProxyCohortPerformance) {
      CSVoptions.push({
        value: "compAIproxyPerformance",
        label: "Comp AI Proxy Cohort (Performance)",
        key: "CompAIProxyCohortPerformance",
      });
    }

    if (customUserPeerGroups && Object.keys(customUserPeerGroups).length) {
      for (const [customPeerGroupKey, customPeerGroupValue] of Object.entries(
        customUserPeerGroups,
      )) {
        CSVoptions.push({
          value: customPeerGroupKey,
          label: customPeerGroupKey,
          tickers: customPeerGroupValue,
          isCustom: true,
        });
      }
    }

    setCSVExportOptions(CSVoptions);
  }, [companyInfo, customUserPeerGroups]);

  const [cohortTableTickers, setCohortTableTickers] = useState([]);

  useMemo(() => {
    const token = localStorage.getItem("ACCESS_TOKEN");
    const ticker = companyInfo?.Ticker;
    const CIK = companyInfo?.CIK;

    const isCustomPeerGroup = CSVExportOptions.find(
      (item) => item.value === CSVExportOptionSelected,
    )?.isCustom;

    const customPeerGroupTickers = isCustomPeerGroup
      ? CSVExportOptions.find((item) => item.value === CSVExportOptionSelected)
          ?.tickers
      : [];

    const cohortKey = CSVExportOptions.find(
      (item) => item.value === CSVExportOptionSelected,
    )?.key;

    const cohortTableTickers = isCustomPeerGroup
      ? customPeerGroupTickers
      : cohortKey
        ? companyInfo?.[cohortKey]
        : [];

    setCohortTableTickers(cohortTableTickers);

    const CIKtickerMap = getCIKtickerMap(
      [ticker, ...cohortTableTickers.filter((item) => item !== ticker)],
      companies,
    );

    if (cohortTableTickers && cohortTableTickers.length) {
      dispatch(getBoardPay(CIKtickerMap, token));
    } else {
      dispatch(getBoardPay(JSON.stringify([{ ticker, CIK }]), token));
    }
  }, [
    companies,
    companyInfo,
    CSVExportOptions,
    CSVExportOptionSelected,
    dispatch,
  ]);

  const yearsAvailable = useMemo(() => {
    const ticker = companyInfo?.Ticker;
    const CIK = companyInfo?.CIK;
    if (!boardPay?.[ticker]?.BoardPay) return [];
    setYearSelected("");
    const token = localStorage.getItem("ACCESS_TOKEN");
    dispatch(getBoardPayProfiles(CIK, token));
    return Object.keys(boardPay?.[ticker]?.BoardPay)
      .map((item, index) => ({ value: item, label: item, key: index }))
      .sort((a, b) => parseInt(b.value) - parseInt(a.value));
  }, [companyInfo, boardPay, dispatch]);

  const [profileYear, setProfileYear] = useState(null);
  useEffect(() => {
    if (yearsAvailable && yearsAvailable.length && yearsAvailable?.[0]?.value) {
      setYearSelected(yearsAvailable[0].value);
      setProfileYear(yearsAvailable[0].value);
    }
  }, [yearsAvailable]);

  useEffect(() => {
    const dataPeekKeys = companyInfo?.BoardPayDataPeek
      ? Object.keys(companyInfo?.BoardPayDataPeek).reverse()
      : [];

    let reportYearEqualsDataYear = true;
    if (
      dataPeekKeys.length &&
      yearsAvailable.length &&
      yearsAvailable[0] !== dataPeekKeys[0] &&
      yearsAvailable.length > dataPeekKeys.length // means there is missing data, so just display the report years instead of guessing
    ) {
      reportYearEqualsDataYear = false;
    }
    setDataPeekLinks(
      dataPeekKeys.map((year) => {
        const content = (
          <div key={year}>
            {companyInfo.BoardPayDataPeek[year]?.url ? (
              <a
                href={companyInfo.BoardPayDataPeek[year].url}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={`/boardpay_peek/${companyInfo.BoardPayDataPeek[year].file}?ver=${version}`}
                  style={{ width: "750px" }}
                  alt={`BoardPayDataPeek data for ${companyInfo?.Ticker} ${
                    reportYearEqualsDataYear ? year : year - 1
                  }`}
                />
              </a>
            ) : (
              <img
                src={`/boardpay_peek/${companyInfo.BoardPayDataPeek[year].file}?ver=${version}`}
                style={{ width: "750px" }}
                alt={`BoardPayDataPeek data for ${companyInfo?.Ticker} ${
                  reportYearEqualsDataYear ? year : year - 1
                }`}
              />
            )}
          </div>
        );
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-end",
            }}
            key={year}
          >
            <Popover
              // style={{ width: 100 }}
              content={content}
              placement="bottomRight"
              title={
                <>
                  <p>
                    Board Pay DataPeek - {companyInfo?.Ticker}{" "}
                    {reportYearEqualsDataYear ? year : year - 1}
                  </p>
                  {companyInfo.BoardPayDataPeek[year]?.conversion && (
                    <p>
                      Data in this table was converted at a rate of{" "}
                      {companyInfo.BoardPayDataPeek[year].conversion.amount}{" "}
                      {companyInfo.BoardPayDataPeek[year].conversion.type} based
                      on report date{" "}
                      {new Date(
                        companyInfo.BoardPayDataPeek[year].conversion?.date,
                      ).toLocaleDateString()}
                    </p>
                  )}
                </>
              }
            >
              <div
                style={{
                  width: 200,
                  cursor: "pointer",
                  paddingRight: "2px",
                  paddingTop: "0px",
                  paddingBottom: "4px",
                  marginTop: "-6px",
                }}
              >
                <p
                  style={{
                    textAlign: "right",
                    color: "var(--blue)",
                  }}
                >
                  DataPeek - {companyInfo?.Ticker}{" "}
                  {reportYearEqualsDataYear ? year : year - 1}
                </p>
              </div>
            </Popover>
          </div>
        );
      }),
    );
  }, [companyInfo, version, yearsAvailable]);

  const boardPayData = useMemo(() => {
    if (!boardPay?.[companyInfo?.Ticker]?.BoardPay) return {};
    const filteredBoardPay = {};
    // console.log(boardPay);
    for (const [boardPayYear, boardPayData] of Object.entries(
      boardPay[companyInfo?.Ticker].BoardPay,
    )) {
      // console.log(boardPayData);
      const filteredBoardPayData = boardPayData.filter(
        (item) => item?.total >= -1,
      );
      filteredBoardPay[boardPayYear] = filteredBoardPayData.map((item) => ({
        Ticker: companyInfo?.Ticker || "",
        Company: companies?.[companyInfo?.Ticker]?.Company || "",
        ...item,
      }));
    }
    return filteredBoardPay;
  }, [companies, companyInfo, boardPay]);

  const peerBoardPayData = useMemo(() => {
    if (!cohortTableTickers?.some((ticker) => boardPay?.[ticker]?.BoardPay)) {
      return {};
    }
    const filteredBoardPay = {};

    // console.log(boardPay);
    const tickerList = !cohortTableTickers?.includes(companyInfo?.Ticker)
      ? [companyInfo.Ticker, ...cohortTableTickers]
      : cohortTableTickers;
    for (const ticker of tickerList) {
      if (!boardPay?.[ticker]?.BoardPay || !companies?.[ticker]?.Company)
        continue;
      for (const [boardPayYear, boardPayData] of Object.entries(
        boardPay?.[ticker]?.BoardPay,
      )) {
        if (!filteredBoardPay[boardPayYear]) {
          filteredBoardPay[boardPayYear] = [];
        }
        // console.log(boardPayData);
        const filteredBoardPayData = boardPayData.filter(
          (item) => item?.total >= -1,
        );
        filteredBoardPay[boardPayYear].push(
          ...filteredBoardPayData.map((item) => ({
            Ticker: ticker || "",
            Company: companies?.[ticker]?.Company || "",
            ...item,
          })),
        );
      }
    }

    return filteredBoardPay;
  }, [companyInfo, companies, boardPay, cohortTableTickers]);

  const dataSource = useMemo(() => {
    if (!boardPayData?.[yearSelected]) return [];
    return boardPayData[yearSelected]
      .filter((item) => item?.total >= -1)
      .map((item, index) => {
        const boardMemberData = { key: index };

        Object.keys(boardPayMap).forEach((key) => {
          // Ensure its own ticker/alternate tickers don't appear here (also done in formatter, this is a backup)
          const appearsInTickers =
            typeof item?.appearsIn === "object" &&
            !Array.isArray(item?.appearsIn)
              ? Object.keys(item.appearsIn)
              : item?.appearsIn?.length
                ? item?.appearsIn
                : [];
          const formattedKey =
            key === "name" && item[key] ? (
              boardPayProfiles?.BoardMembers?.some(
                (member) =>
                  slugifyName(member?.Name) === slugifyName(item[key]),
              ) ? (
                <Link
                  to={`/company/${companyInfo.Ticker}/board/${slugifyName(
                    item[key],
                  )}`}
                >
                  {item[key].replace("*", "")}
                </Link>
              ) : item[key] ? (
                item[key].replace("*", "")
              ) : undefined
            ) : key === "count" ? (
              appearsInTickers
            ) : (
              formatMeasure(item[key], "dollar")
            );
          if (key === "name") {
            boardMemberData["actualName"] = item?.["name"]
              ? item?.["name"].replace("*", "")
              : undefined;
          }
          boardMemberData[key] = formattedKey || emdash;
        });
        return boardMemberData;
      });
  }, [boardPayData, boardPayProfiles, companyInfo, yearSelected]);

  const additionalDataSource = useMemo(() => {
    if (!boardPayProfiles?.BoardMembers || !dataSource) return [];
    return boardPayProfiles?.BoardMembers.filter(
      (member) =>
        !dataSource.some(
          (item) =>
            item?.actualName &&
            member?.Name &&
            slugifyName(item.actualName) === slugifyName(member.Name),
        ) && yearSelected > member?.MemberSince,
    ).map((item, index) => {
      item.name = item.Name || "";
      const boardMemberData = { key: index + dataSource.length + 1 };

      Object.keys(boardPayMap).forEach((key) => {
        // Ensure its own ticker/alternate tickers don't appear here (also done in formatter, this is a backup)
        const appearsInTickers =
          typeof item?.appearsIn === "object" && !Array.isArray(item?.appearsIn)
            ? Object.keys(item.appearsIn)
            : item?.appearsIn?.length
              ? item?.appearsIn
              : [];
        const formattedKey =
          key === "name" ? (
            <Link
              to={`/company/${companyInfo.Ticker}/board/${slugifyName(
                item[key],
              )}`}
            >
              {item[key]}
            </Link>
          ) : key === "count" ? (
            appearsInTickers
          ) : (
            item?.compensation?.[key]
          );
        if (key === "name") {
          boardMemberData["actualName"] = item?.["name"]
            ? item?.["name"].replace("*", "")
            : undefined;
        }
        boardMemberData[key] = formattedKey || emdash;
      });
      return boardMemberData;
    });
  }, [boardPayProfiles, companyInfo, dataSource, yearSelected]);

  const [pageContainsColumns, setPageContainsColumns] = useState([]);

  useEffect(() => {
    const columnsFound = [];
    dataSource.forEach((item) => {
      const boardMember = boardPayProfiles?.BoardMembers?.find((member) => {
        if (member?.Name && item?.actualName) {
          return slugifyName(item.actualName) === slugifyName(member.Name);
        }
        return undefined;
      });
      if (
        boardMember?.Committees?.filter((item) => item?.includes("(Chair)"))
          ?.length &&
        !columnsFound.includes("Committee Chair")
      ) {
        if (profileYear === yearSelected) {
          columnsFound.push("Committee Chair");
        }
      }
      if (
        boardMember?.Committees?.filter((item) => !item?.includes("(Chair)"))
          ?.length &&
        !columnsFound.includes("Committee(s)")
      ) {
        if (profileYear === yearSelected) {
          columnsFound.push("Committee(s)");
        }
      }
      if (boardMember?.Age && !columnsFound.includes("Age")) {
        columnsFound.push("Age");
      }
      if (boardMember?.Type && !columnsFound.includes("Member Type")) {
        if (profileYear === yearSelected) {
          columnsFound.push("Member Type");
        }
      }
      if (boardMember?.MemberSince && !columnsFound.includes("Member Since")) {
        columnsFound.push("Member Since");
      }
    });
    setPageContainsColumns(columnsFound);
  }, [dataSource, boardPayProfiles, profileYear, yearSelected]);

  const columns = Object.entries(boardPayMap).map(([key, titles]) => ({
    title:
      key === "name" || key === "total" ? (
        <b key={key}>{titles[0]}</b>
      ) : (
        <span key={key}>{titles[0]}</span>
      ),
    dataIndex: key,
    key: key,
    width: key === "count" ? 10 : "",
    render: (item) =>
      key === "count" ? (
        <>
          {item.length ? (
            <Tooltip
              title={
                <div style={{ textAlign: "center" }}>
                  <p>Board Appearances Ticker{item.length > 1 ? "s" : ""}:</p>
                  {item.map((item, index) =>
                    companies[item] ? (
                      <p key={index}>
                        <Link
                          to={`/company/${item}`}
                          style={{ color: "var(--light-blue" }}
                        >
                          {item}
                        </Link>
                      </p>
                    ) : (
                      <p>{item}</p>
                    ),
                  )}
                </div>
              }
              overlayClassName="white-link"
            >
              <span style={{ cursor: "help" }}>{item.length}</span>
            </Tooltip>
          ) : (
            <span>{emdash}</span>
          )}
        </>
      ) : key === "name" ? (
        <b>{item}</b>
      ) : key === "total" ? (
        <b>{item}</b>
      ) : (
        <span>{item}</span>
      ),
  }));

  const filteredColumns = useMemo(() => {
    const optionalColumns = [
      "Age",
      "Member Since",
      "Member Type",
      "Committee(s)",
      "Committee Chair",
    ];
    const newColumns = columns.filter((col) => {
      return dataSource.some((item) => item[col.key] !== emdash);
    });
    newColumns.splice(
      2,
      0,
      ...[
        {
          title: "Age",
          dataIndex: "Age",
          key: "Age",
          render: (item, row) => {
            const boardMemberAge = boardPayProfiles?.BoardMembers?.find(
              (member) => {
                if (member?.Name && row?.actualName) {
                  return (
                    slugifyName(row.actualName) === slugifyName(member.Name)
                  );
                }
                return undefined;
              },
            )?.Age;
            return boardMemberAge
              ? boardMemberAge + (yearSelected - profileYear)
              : emdash;
          },
        },
        {
          title: "Member Since",
          dataIndex: "Member Since",
          key: "Member Since",
          render: (item, row) => {
            const memberSinceYear = boardPayProfiles?.BoardMembers?.find(
              (member) => {
                if (member?.Name && row?.actualName) {
                  return (
                    slugifyName(row.actualName) === slugifyName(member.Name)
                  );
                }
                return undefined;
              },
            )?.MemberSince;
            return memberSinceYear && yearSelected >= memberSinceYear
              ? memberSinceYear
              : emdash;
          },
        },
        {
          title: "Member Type",
          dataIndex: "Member Type",
          key: "Member Type",
          render: (item, row) => {
            const boardMemberDetails = boardPayProfiles?.BoardMembers?.find(
              (member) => {
                if (member?.Name && row?.actualName) {
                  return (
                    slugifyName(row.actualName) === slugifyName(member.Name)
                  );
                }
                return undefined;
              },
            );
            const BoardMemberType = boardMemberDetails?.Type;
            const BoardMemberNotes = boardMemberDetails?.TypeNotes;
            const isLeadDirector = boardMemberDetails?.LeadDirector;
            return BoardMemberType ? (
              <div>
                <Tooltip title={BoardMemberNotes || ""}>
                  <div style={{ cursor: BoardMemberNotes ? "help" : "" }}>
                    <b>{isLeadDirector ? "Lead Director" : ""}</b>
                    {isLeadDirector ? ",\n" : ""}
                    {BoardMemberType}
                    {BoardMemberNotes ? " *" : ""}
                  </div>
                </Tooltip>
              </div>
            ) : (
              <span>{emdash}</span>
            );
          },
        },
        {
          title: "Committee(s)",
          dataIndex: "Committee(s)",
          key: "Committee(s)",
          render: (item, row) => {
            const boardMemberChairs = boardPayProfiles?.BoardMembers?.find(
              (member) => {
                if (member?.Name && row?.actualName) {
                  return (
                    slugifyName(row.actualName) === slugifyName(member.Name)
                  );
                }
                return undefined;
              },
            )?.Committees?.filter((item) => !item?.includes("(Chair)"));
            return boardMemberChairs?.length
              ? boardMemberChairs?.map((item) => <p>{item}</p>) || emdash
              : emdash;
          },
        },
        {
          title: "Committee Chair",
          dataIndex: "Committee Chair",
          key: "Committee Chair",
          render: (item, row) => {
            const boardMemberChairs = boardPayProfiles?.BoardMembers?.find(
              (member) => {
                if (member?.Name && row?.actualName) {
                  return (
                    slugifyName(row.actualName) === slugifyName(member.Name)
                  );
                }
                return undefined;
              },
            )?.Committees?.filter((item) => item?.includes("(Chair)"));
            return boardMemberChairs?.length
              ? boardMemberChairs?.map((item) => (
                  <p>{item.replace(" (Chair)", "")}</p>
                )) || emdash
              : emdash;
          },
        },
      ],
    );

    return newColumns.filter(
      (item) =>
        !optionalColumns.includes(item.title) ||
        pageContainsColumns.includes(item.title),
    );
  }, [
    columns,
    dataSource,
    boardPayProfiles,
    pageContainsColumns,
    profileYear,
    yearSelected,
  ]);

  const yearsOptionsForBarGraph = useMemo(() => {
    return yearsAvailable
      .map((item) => item.value)
      .map((year, index) => (
        <option value={year} key={index}>
          {year}
        </option>
      ));
  }, [yearsAvailable]);

  const hasBoardPayData = useMemo(() => {
    return yearsAvailable
      .map((item) => item.value)
      .some((year) => boardPayData[year].some((member) => member?.name));
  }, [boardPayData, yearsAvailable]);

  const formattedDataForLineGraph = useMemo(() => {
    if (!hasBoardPayData) return {};
    const lineGraphData = {};
    const formattedBoardPayMembers = {};
    for (const [year, boardPayMembers] of Object.entries(boardPayData)) {
      for (const boardPayMember of boardPayMembers) {
        if (!formattedBoardPayMembers?.[boardPayMember?.name]) {
          formattedBoardPayMembers[boardPayMember?.name] = {};
          formattedBoardPayMembers[boardPayMember?.name][year] = boardPayMember;
        } else {
          formattedBoardPayMembers[boardPayMember?.name][year] = boardPayMember;
        }
      }
    }
    const maxYear = Math.max(
      ...yearsAvailable.map((item) => parseInt(item.value)),
    );
    const minYear = Math.min(
      ...yearsAvailable.map((item) => parseInt(item.value)),
    );
    for (let year = minYear; year <= maxYear; year++) {
      lineGraphData[year] = [];
      for (const [name, data] of Object.entries(formattedBoardPayMembers)) {
        if (data?.[year]) {
          lineGraphData[year].push({ name, compensation: data });
        }
      }
    }
    return lineGraphData;
  }, [boardPayData, yearsAvailable, hasBoardPayData]);

  const boardPayForCSVExport = useCallback(
    (boardPayData) => {
      if (!boardPayData) return;
      const allBoardPayData = [];
      for (const yearObject of yearsAvailable) {
        const year = yearObject.value;
        if (boardPayData?.[year]?.length) {
          allBoardPayData.push(
            ...boardPayData?.[year].map((item) => {
              // delete item?.appearsIn;
              delete item?.[year];
              delete item?.[""];
              return { year, ...item };
            }),
          );
        }
      }
      const allBoardPayHeaderKeys = [];
      allBoardPayData.forEach((item) => {
        for (const key of Object.keys(item)) {
          if (!allBoardPayHeaderKeys.includes(key)) {
            allBoardPayHeaderKeys.push(key);
          }
        }
      });
      const sortedBoardPayHeaderKeys = allBoardPayHeaderKeys.sort(
        (a, b) =>
          Object.keys(boardPayMap).indexOf(a) -
          Object.keys(boardPayMap).indexOf(b),
      );
      const formattedBoardPayData = allBoardPayData.map((item) => {
        const formattedItem = {};
        formattedItem["Ticker"] = item?.Ticker || "";
        formattedItem["Company"] = `"${item?.Company}"` || "";

        sortedBoardPayHeaderKeys.forEach((key) => {
          if (key && key !== "DataPeek" && Array.isArray(boardPayMap[key])) {
            formattedItem[boardPayMap[key][0]] =
              item?.[key] || item?.[key] >= 0 ? item[key] : "";
          }
          if (key && key === "count" && formattedItem[boardPayMap[key]] > 0) {
            formattedItem[boardPayMap[key]] -= 1;
          }
        });
        return formattedItem;
      });
      return formattedBoardPayData;
    },
    [yearsAvailable],
  );

  const CSVselector = () => {
    return (
      <Select
        options={CSVExportOptions}
        value={CSVExportOptionSelected}
        onChange={(value) => setCSVExportOptionSelected(value)}
        style={{
          width:
            (customUserPeerGroups &&
              Object.keys(customUserPeerGroups).length) ||
            CSVExportOptions.some(
              (item) =>
                item.label.includes("Performance") ||
                item.label.includes("Compensation"),
            )
              ? 230
              : 150,
          textAlign: "center",
          paddingRight: 5,
        }}
        dropdownStyle={{ textAlign: "center" }}
        defaultValue={"company"}
      ></Select>
    );
  };

  return (
    <>
      {!isTier2 ? (
        <div
          className="comp-ai-section col-lg-9 executives "
          style={{ height: "100%", padding: 50 }}
        >
          <h3
            style={{
              textAlign: "center",
              fontWeight: "normal",
              fontSize: 22,
            }}
          >
            <p style={{ fontWeight: "bold" }}>
              This page is for C-Suite Comp&reg; Enterprise users only.
            </p>
            <br />
            <p>
              Contact your account manager to access Board Pay data and analysis
              on how board members across the nation are being compensated and
              intelligence on interlocking board positions.
            </p>
          </h3>
        </div>
      ) : (
        <>
          <div className="company-calculator-section col-lg-12 executives p-0">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
                marginLeft: "20px",
                width: "100%",
              }}
            >
              <div style={{ width: "100%" }}>
                <h2 style={{ textAlign: "center" }}>Board Pay</h2>
              </div>
              {!hasBoardPayData ? (
                <p style={{ textAlign: "center" }}>
                  Board Pay is not currently available for{" "}
                  {companyInfo?.Company
                    ? `${companyInfo.Company}${
                        companyInfo.Company.endsWith(".") ? "" : "."
                      }`
                    : "this company."}
                </p>
              ) : (
                <>
                  <div style={{ width: 350, display: "flex" }}>
                    <h3
                      style={{
                        fontSize: 20,
                        marginRight: 10,
                        marginTop: 4,
                        textAlign: "bottom",
                      }}
                    >
                      Select Report Year:{" "}
                    </h3>
                    <Select
                      defaultValue={
                        yearsAvailable?.length ? yearsAvailable?.[0]?.value : ""
                      }
                      style={{ width: 120, marginBottom: 20 }}
                      options={yearsAvailable}
                      onChange={(value) => setYearSelected(value)}
                      value={yearSelected}
                    />
                  </div>
                  <div className="container">
                    <div className="row" style={{ marginBottom: "-40px" }}>
                      <div className="col-md justify-content-end align-items-end ">
                        {dataPeekLinks}
                      </div>
                    </div>
                    <div className="row">
                      {!PDFprocessing && (
                        <div className="container">
                          <div className="col-md d-flex justify-content-start align-items-end pb-3">
                            <span
                              style={{
                                fontSize: 20,
                                lineHeight: 1.6,
                              }}
                            >
                              CSV&nbsp;Export:&nbsp;
                            </span>
                            <CSVselector />
                            <Button
                              // style={{ marginBottom: 10 }}
                              onClick={() =>
                                exportToCSV(
                                  boardPayForCSVExport({
                                    ...boardPayData,
                                    ...peerBoardPayData,
                                  }),
                                  `${companyInfo?.Ticker} ${
                                    CSVExportOptionSelected !== "company"
                                      ? CSVExportOptionSelected.slice(
                                          0,
                                          1,
                                        ).toUpperCase() +
                                        CSVExportOptionSelected.slice(
                                          1,
                                          CSVExportOptionSelected.length,
                                        ) +
                                        " Cohort"
                                      : ""
                                  } - Board Pay ${
                                    new Date().toString().split("GMT-")[0]
                                  }.csv`,
                                )
                              }
                            >
                              Export All Board Pay Data as CSV
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <Table
                    dataSource={[...dataSource, ...additionalDataSource]}
                    columns={filteredColumns}
                    pagination={{
                      hideOnSinglePage: true,
                      pageSize: 100,
                    }}
                    style={{ width: "100%" }}
                  />
                  <div className="c-table">
                    <div
                      className="graphs"
                      style={{
                        width: "100%",
                        margin: "20px 0 0 0 ",
                        height: 425,
                      }}
                    >
                      <BarGraphOverview
                        data={boardPayData?.[yearSelected]}
                        year={yearSelected}
                        setYear={setYearSelected}
                        yearsOptions={yearsOptionsForBarGraph}
                        company={companyInfo?.Company}
                        isBoardPay={true}
                        PDFprocessing={PDFprocessing}
                      />
                    </div>
                    <div
                      className="graphs"
                      style={{ width: "100%", margin: 0 }}
                    >
                      <LineGraph
                        data={formattedDataForLineGraph}
                        currentYear={yearSelected}
                        company={companyInfo?.Company}
                        isBoardPay={true}
                        PDFprocessing={PDFprocessing}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
